<template>
    <div class="message-container">
      <!-- 左侧患者列表 -->
      <div class="patient-list">
        <div class="search-container">
          <el-input
            placeholder="搜索患者姓名/病历号"
            v-model="searchQuery"
            prefix-icon="el-icon-search"
            clearable
            @input="filterPatients"
          >
          </el-input>
        </div>
  
        <!-- 项目筛选下拉框 -->
        <div class="filter-section">
          <el-select
            v-model="projectFilter"
            placeholder="按项目筛选"
            size="small"
            style="width: 100%"
            clearable
          >
            <el-option
              v-for="item in projectOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </div>
  
        <!-- 疾病分类筛选 -->
        <div class="filter-section">
          <el-select
            v-model="diseaseFilter"
            placeholder="按疾病分类"
            size="small"
            style="width: 100%"
            clearable
          >
            <el-option
              v-for="item in diseaseOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </div>
  
        <el-tabs v-model="patientListTab" type="card" @tab-click="handleTabClick">
          <el-tab-pane label="全部患者" name="all"></el-tab-pane>
          <el-tab-pane label="待回复" name="pending">
            <span slot="label"
              >待回复
              <el-badge
                :value="getPendingCount()"
                class="pending-badge"
                :hidden="getPendingCount() === 0"
              ></el-badge
            ></span>
          </el-tab-pane>
          <el-tab-pane label="项目异常" name="abnormal">
            <span slot="label">项目异常</span>
          </el-tab-pane>
        </el-tabs>
  
        <div class="patient-list-content">
          <div
            v-for="patient in filteredPatients"
            :key="patient.id"
            :class="[
              'patient-item',
              selectedPatient && patient.id === selectedPatient.id
                ? 'active'
                : '',
              patient.status === '异常' ? 'abnormal-patient' : '',
            ]"
            @click="selectPatient(patient)"
          >
            <div class="avatar-container">
              <el-avatar :src="patient.avatar" :size="40"></el-avatar>
              <el-badge
                :value="getPatientUnreadCount(patient)"
                :hidden="getPatientUnreadCount(patient) === 0"
                class="item-unread"
              ></el-badge>
              <el-tag
                v-if="patient.status === '异常'"
                size="mini"
                type="danger"
                class="item-abnormal"
                >异常</el-tag
              >
            </div>
            <div class="patient-info">
              <div class="patient-name">
                {{ patient.name }}
                <span class="patient-project">[{{ patient.project }}]</span>
              </div>
              <div class="patient-disease">{{ patient.diagnosis }}</div>
              <div class="latest-message">{{ getLatestMessage(patient) }}</div>
            </div>
            <div class="message-time">{{ getLatestMessageTime(patient) }}</div>
          </div>
        </div>
      </div>
  
      <!-- 中间消息区域 -->
      <div class="message-area">
        <div v-if="selectedPatient" class="message-header">
          <div class="patient-title">
            <span class="name">{{ selectedPatient.name }}</span>
            <span class="record-number"
              >病历号: {{ selectedPatient.recordNum }}</span
            >
            <el-tag size="small" :type="getStatusType(selectedPatient.status)">
              {{ selectedPatient.status }}
            </el-tag>
            <el-tag size="small" type="primary" style="margin-left: 5px">
              {{ selectedPatient.project }}
            </el-tag>
          </div>
          <div class="header-actions">
            <el-dropdown @command="handlePatientAction" trigger="click">
              <el-button type="text">
                更多操作<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="phone">电话联系</el-dropdown-item>
                <el-dropdown-item command="record">查看随访记录</el-dropdown-item>
                <el-dropdown-item command="plan">查看随访计划</el-dropdown-item>
                <el-dropdown-item command="report">项目报告</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-content">
          <div
            v-for="(message, index) in selectedPatient.messages"
            :key="index"
            class="message-block"
          >
            <div
              class="message-date-divider"
              v-if="showDateDivider(message, index)"
            >
              <span>{{ formatFullDate(message.timestamp) }}</span>
            </div>
  
            <div
              :class="[
                'message',
                message.fromPatient ? 'patient-message' : 'staff-message',
              ]"
            >
              <div class="message-sender">
                <span>{{ message.sender }}</span>
                <span class="sender-role" v-if="message.role"
                  >[{{ message.role }}]</span
                >
                <span class="message-time">{{
                  formatTime(message.timestamp)
                }}</span>
              </div>
  
              <div class="message-bubble">
                <div class="message-content-text" v-html="message.content"></div>
  
                <!-- 图片展示 -->
                <div
                  v-if="message.images && message.images.length > 0"
                  class="message-images"
                >
                  <el-image
                    v-for="(img, imgIndex) in message.images"
                    :key="imgIndex"
                    :src="img"
                    :preview-src-list="message.images"
                    fit="cover"
                    class="message-image"
                  ></el-image>
                </div>
  
                <!-- 附件展示 -->
                <div
                  v-if="message.attachments && message.attachments.length > 0"
                  class="message-attachments"
                >
                  <div
                    v-for="(attachment, attIndex) in message.attachments"
                    :key="attIndex"
                    class="attachment-item"
                  >
                    <i class="el-icon-document"></i>
                    <span>{{ attachment.name }}</span>
                    <span class="attachment-size">{{ attachment.size }}</span>
                    <el-button
                      type="text"
                      size="mini"
                      @click="downloadAttachment(attachment)"
                      >下载</el-button
                    >
                  </div>
                </div>
  
                <!-- 消息处理状态 -->
                <div v-if="message.status" class="message-status">
                  <div class="status-info">
                    <i class="el-icon-info"></i>
                    <span>{{
                      message.status === "replied"
                        ? "已回复"
                        : message.status === "forwarded"
                        ? "已转发"
                        : message.status
                    }}</span>
                  </div>
                </div>
  
                <!-- 转发信息 -->
                <div v-if="message.forwardInfo" class="forward-info">
                  <el-alert
                    :title="message.forwardInfo.title"
                    :type="message.forwardInfo.type"
                    :description="message.forwardInfo.desc"
                    show-icon
                    :closable="false"
                    size="small"
                  ></el-alert>
                </div>
  
                <!-- 协作人信息 -->
                <div
                  v-if="message.collaborators && message.collaborators.length > 0"
                  class="collaborators-info"
                >
                  <span class="collaborator-label">协作人:</span>
                  <el-tag
                    v-for="(collab, cIndex) in message.collaborators"
                    :key="cIndex"
                    size="mini"
                    style="margin-right: 5px"
                  >
                    {{ collab }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div v-if="selectedPatient" class="message-reply">
          <div class="reply-toolbar">
            <el-button
              type="text"
              icon="el-icon-picture"
              @click="handleUploadImage"
              >添加图片</el-button
            >
            <el-button
              type="text"
              icon="el-icon-paperclip"
              @click="handleUploadFile"
              >添加附件</el-button
            >
  
            <!-- 快捷回复模板 -->
            <el-dropdown @command="handleTemplateCommand" trigger="click">
              <el-button type="text">
                快捷模板<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="(template, index) in replyTemplates"
                  :key="index"
                  :command="index"
                  >{{ template.title }}</el-dropdown-item
                >
                <el-dropdown-item divided command="manage"
                  >管理模板</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
  
            <!-- @ 协作人 -->
            <el-dropdown @command="handleMentionCommand" trigger="click">
              <el-button type="text">
                @协作人<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="(staff, index) in staffOptions"
                  :key="index"
                  :command="staff.value"
                  >{{ staff.label }}</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
  
            <!-- 转发给... -->
            <el-dropdown @command="handleForwardCommand" trigger="click">
              <el-button type="text">
                转发给...<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-for="option in forwardOptions"
                  :key="option.value"
                  :command="option.value"
                  >{{ option.label }}</el-dropdown-item
                >
              </el-dropdown-menu>
            </el-dropdown>
          </div>
  
          <el-input
            type="textarea"
            :rows="3"
            placeholder="请输入回复内容..."
            v-model="replyContent"
            resize="none"
          ></el-input>
  
          <div class="upload-preview" v-if="uploadFiles.length > 0">
            <div
              v-for="(file, index) in uploadFiles"
              :key="index"
              class="upload-file"
            >
              <span>{{ file.name }}</span>
              <el-button
                type="text"
                icon="el-icon-delete"
                @click="removeUploadFile(index)"
              ></el-button>
            </div>
          </div>
  
          <div v-if="forwardTarget" class="forward-alert">
            <el-alert
              title="留言将被转发"
              :description="'接收人: ' + getForwardTargetName(forwardTarget)"
              type="info"
              show-icon
              size="small"
            >
            </el-alert>
          </div>
  
          <div v-if="mentionedStaff.length > 0" class="mention-alert">
            <el-alert
              title="已@协作人"
              :description="'将通知: ' + mentionedStaffNames.join(', ')"
              type="warning"
              show-icon
              size="small"
            >
            </el-alert>
          </div>
  
          <div class="reply-actions">
            <el-button
              type="primary"
              @click="sendReply"
              :disabled="!canSendReply"
            >
              {{ forwardTarget ? "转发留言" : "发送回复" }}
            </el-button>
          </div>
        </div>
  
        <div v-if="!selectedPatient" class="no-patient-selected">
          <i class="el-icon-message"></i>
          <p>请选择一个患者开始处理留言</p>
        </div>
      </div>
  
      <!-- 右侧快捷操作区 -->
      <div class="operation-area" v-if="selectedPatient">
        <div class="operation-header">
          <h3>快捷操作</h3>
        </div>
  
        <el-divider content-position="left">随访工具</el-divider>
  
        <div class="operation-buttons">
          <el-button
            type="primary"
            plain
            icon="el-icon-document"
            @click="sendQuestionnaire"
            >发送随访问卷</el-button
          >
          <el-button
            type="success"
            plain
            icon="el-icon-s-data"
            @click="sendScaleForm"
            >发送评估量表</el-button
          >
          <el-button
            type="warning"
            plain
            icon="el-icon-message"
            @click="sendSmsNotification"
            >发送短信通知</el-button
          >
        </div>
  
        <el-divider content-position="left">随访计划调整</el-divider>
  
        <div class="plan-adjustment">
          <div class="plan-item">
            <el-date-picker
              v-model="nextFollowUpDate"
              type="date"
              placeholder="选择下次随访日期"
              size="small"
              style="width: 100%"
            >
            </el-date-picker>
          </div>
  
          <div class="plan-item">
            <el-select
              v-model="followUpType"
              placeholder="随访方式"
              size="small"
              style="width: 100%"
            >
              <el-option label="门诊随访" value="clinic"></el-option>
              <el-option label="电话随访" value="phone"></el-option>
              <el-option label="线上随访" value="online"></el-option>
              <el-option label="家访" value="home"></el-option>
            </el-select>
          </div>
  
          <div class="plan-item">
            <el-button
              type="primary"
              size="small"
              style="width: 100%"
              @click="updateFollowUpPlan"
              >更新随访计划</el-button
            >
          </div>
        </div>
  
        <el-divider content-position="left">项目管理</el-divider>
  
        <div class="project-management">
          <el-button
            type="info"
            plain
            icon="el-icon-s-cooperation"
            @click="assignDoctor"
            >分配医生</el-button
          >
          <el-button type="info" plain icon="el-icon-user" @click="assignManager"
            >分配个案管理师</el-button
          >
          <el-button
            type="danger"
            plain
            icon="el-icon-warning"
            @click="reportIssue"
            >报告异常</el-button
          >
        </div>
  
        <el-divider content-position="left">项目数据</el-divider>
  
        <div class="patient-metrics">
          <el-row :gutter="10">
            <el-col :span="12">
              <div class="metric-card">
                <div class="metric-title">满意度</div>
                <div class="metric-value">
                  <el-rate
                    v-model="selectedPatient.satisfaction"
                    disabled
                    text-color="#ff9900"
                  ></el-rate>
                </div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="metric-card">
                <div class="metric-title">依从性</div>
                <el-progress
                  :percentage="selectedPatient.compliance"
                  :color="getComplianceColor(selectedPatient.compliance)"
                ></el-progress>
              </div>
            </el-col>
          </el-row>
  
          <el-row :gutter="10" style="margin-top: 10px">
            <el-col :span="12">
              <div class="metric-card">
                <div class="metric-title">随访完成率</div>
                <div class="metric-value">
                  <el-progress
                    :percentage="selectedPatient.followUpCompletion || 65"
                    :color="
                      getComplianceColor(selectedPatient.followUpCompletion || 65)
                    "
                  ></el-progress>
                </div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="metric-card">
                <div class="metric-title">项目进度</div>
                <div class="metric-value">
                  <el-progress
                    :percentage="selectedPatient.projectProgress || 70"
                    :color="
                      getComplianceColor(selectedPatient.projectProgress || 70)
                    "
                  ></el-progress>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>
  
        <el-divider content-position="left">项目状态</el-divider>
  
        <el-timeline>
          <el-timeline-item
            v-for="(activity, index) in selectedPatient.projectActivities ||
            defaultProjectActivities"
            :key="index"
            :timestamp="activity.time"
            :color="activity.color || '#409EFF'"
            :size="activity.size || 'normal'"
          >
            {{ activity.content }}
          </el-timeline-item>
        </el-timeline>
      </div>
  
      <!-- 模板管理对话框 -->
      <el-dialog
        title="快捷回复模板管理"
        :visible.sync="templateDialogVisible"
        width="600px"
      >
        <el-table :data="replyTemplates" style="width: 100%">
          <el-table-column prop="title" label="模板名称"></el-table-column>
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="editTemplate(scope.row)"
                >编辑</el-button
              >
              <el-button
                size="mini"
                type="text"
                @click="deleteTemplate(scope.$index)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
        <div class="dialog-footer" slot="footer">
          <el-button @click="templateDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="addNewTemplate">新增模板</el-button>
        </div>
      </el-dialog>
  
      <!-- 编辑模板对话框 -->
      <el-dialog
        :title="currentTemplateAction === 'add' ? '新增模板' : '编辑模板'"
        :visible.sync="editTemplateDialogVisible"
        width="600px"
      >
        <el-form :model="currentTemplate" label-width="80px">
          <el-form-item label="模板名称">
            <el-input v-model="currentTemplate.title"></el-input>
          </el-form-item>
          <el-form-item label="模板内容">
            <el-input
              type="textarea"
              :rows="5"
              v-model="currentTemplate.content"
            ></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="editTemplateDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="saveTemplate">确 定</el-button>
        </div>
      </el-dialog>
  
      <!-- 发送问卷对话框 -->
      <el-dialog
        title="发送随访问卷"
        :visible.sync="questionnaireDialogVisible"
        width="600px"
      >
        <el-form label-width="100px">
          <el-form-item label="选择问卷">
            <el-select
              v-model="selectedQuestionnaire"
              placeholder="请选择问卷"
              style="width: 100%"
            >
              <el-option
                v-for="item in questionnaireOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="截止日期">
            <el-date-picker
              v-model="questionnaireDeadline"
              type="date"
              placeholder="选择截止日期"
              style="width: 100%"
            >
            </el-date-picker>
          </el-form-item>
          <el-form-item label="提醒方式">
            <el-checkbox-group v-model="reminderMethods">
              <el-checkbox label="sms">短信提醒</el-checkbox>
              <el-checkbox label="app">APP推送</el-checkbox>
              <el-checkbox label="wechat">微信提醒</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="备注说明">
            <el-input
              type="textarea"
              :rows="3"
              v-model="questionnaireRemark"
            ></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="questionnaireDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="confirmSendQuestionnaire"
            >确认发送</el-button
          >
        </div>
      </el-dialog>
  
      <!-- 分配医生对话框 -->
      <el-dialog
        title="分配医生"
        :visible.sync="assignDoctorDialogVisible"
        width="500px"
      >
        <el-form label-width="80px">
          <el-form-item label="选择医生">
            <el-select
              v-model="selectedDoctor"
              placeholder="请选择医生"
              style="width: 100%"
            >
              <el-option
                v-for="doctor in doctorOptions"
                :key="doctor.value"
                :label="doctor.label"
                :value="doctor.value"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="分配原因">
            <el-input type="textarea" :rows="3" v-model="assignReason"></el-input>
          </el-form-item>
          <el-form-item label="通知医生">
            <el-switch v-model="notifyAssignee"></el-switch>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="assignDoctorDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="confirmAssignDoctor"
            >确认分配</el-button
          >
        </div>
      </el-dialog>
  
      <!-- 异常报告对话框 -->
      <el-dialog
        title="报告异常情况"
        :visible.sync="issueReportDialogVisible"
        width="600px"
      >
        <el-form label-width="100px">
          <el-form-item label="异常类型">
            <el-select
              v-model="issueType"
              placeholder="请选择异常类型"
              style="width: 100%"
            >
              <el-option label="随访依从性异常" value="compliance"></el-option>
              <el-option label="生命体征异常" value="vital_signs"></el-option>
              <el-option label="不良反应" value="adverse_event"></el-option>
              <el-option label="项目执行异常" value="process"></el-option>
              <el-option label="其他异常" value="other"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="紧急程度">
            <el-rate
              v-model="issueSeverity"
              :colors="['#67C23A', '#E6A23C', '#F56C6C']"
            ></el-rate>
          </el-form-item>
          <el-form-item label="异常描述">
            <el-input
              type="textarea"
              :rows="4"
              v-model="issueDescription"
            ></el-input>
          </el-form-item>
          <el-form-item label="处理建议">
            <el-input
              type="textarea"
              :rows="3"
              v-model="issueSuggestion"
            ></el-input>
          </el-form-item>
          <el-form-item label="通知对象">
            <el-checkbox-group v-model="issueNotifyTargets">
              <el-checkbox label="department">科室主任</el-checkbox>
              <el-checkbox label="doctor">主治医生</el-checkbox>
              <el-checkbox label="manager">个案管理师</el-checkbox>
            </el-checkbox-group>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="issueReportDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="confirmReportIssue"
            >提交报告</el-button
          >
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "ProjectManagerMessage",
    data() {
      return {
        // 当前项目管理师信息
        currentManager: {
          id: "M001",
          name: "赵主任",
          avatar:
            "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
          department: "随访中心",
          title: "项目管理师",
        },
  
        // 过滤选项
        projectFilter: "",
        diseaseFilter: "",
  
        // 搜索查询
        searchQuery: "",
        patientListTab: "all",
  
        // 选中的患者
        selectedPatient: null,
  
        // 回复内容
        replyContent: "",
        uploadFiles: [],
        forwardTarget: null, // 转发目标
        mentionedStaff: [], // @的人员
  
        // 随访计划
        nextFollowUpDate: null,
        followUpType: "",
  
        // 问卷相关
        questionnaireDialogVisible: false,
        selectedQuestionnaire: "",
        questionnaireDeadline: null,
        reminderMethods: ["sms", "app"],
        questionnaireRemark: "",
  
        // 分配医生相关
        assignDoctorDialogVisible: false,
        selectedDoctor: "",
        assignReason: "",
        notifyAssignee: true,
  
        // 异常报告相关
        issueReportDialogVisible: false,
        issueType: "",
        issueSeverity: 3,
        issueDescription: "",
        issueSuggestion: "",
        issueNotifyTargets: ["doctor", "manager"],
  
        // 对话框
        templateDialogVisible: false,
        editTemplateDialogVisible: false,
  
        // 模板相关
        currentTemplateAction: "add",
        currentTemplateIndex: -1,
        currentTemplate: {
          title: "",
          content: "",
        },
  
        // 项目筛选选项
        projectOptions: [
          { value: "cardiac", label: "心脏康复项目" },
          { value: "diabetes", label: "糖尿病管理项目" },
          { value: "oncology", label: "肿瘤随访项目" },
          { value: "stroke", label: "卒中康复项目" },
          { value: "copd", label: "慢阻肺管理项目" },
        ],
  
        // 疾病分类选项
        diseaseOptions: [
          { value: "heart", label: "心血管疾病" },
          { value: "diabetes", label: "内分泌疾病" },
          { value: "cancer", label: "肿瘤疾病" },
          { value: "neuro", label: "神经系统疾病" },
          { value: "respiratory", label: "呼吸系统疾病" },
        ],
  
        // 问卷选项
        questionnaireOptions: [
          { value: "q1", label: "心脏病患者生活质量调查问卷" },
          { value: "q2", label: "糖尿病自我管理调查问卷" },
          { value: "q3", label: "肿瘤患者随访评估量表" },
          { value: "q4", label: "慢阻肺症状评估问卷" },
          { value: "q5", label: "术后康复情况随访问卷" },
        ],
  
        // 医生选项
        doctorOptions: [
          { value: "d1", label: "张医生（心内科主治医师）" },
          { value: "d2", label: "王医生（内分泌科副主任医师）" },
          { value: "d3", label: "李医生（肿瘤科主任医师）" },
          { value: "d4", label: "赵医生（神经内科主治医师）" },
          { value: "d5", label: "刘医生（呼吸科副主任医师）" },
        ],
  
        // 快捷回复模板
        replyTemplates: [
          {
            title: "随访项目进展通知",
            content:
              "您好，您参与的随访项目已进行到第X阶段，根据项目规划，需要您进行下一次随访检查。请点击链接查看详情并按要求完成相关检查。如有疑问，请随时联系我们。",
          },
          {
            title: "问卷完成提醒",
            content:
              "您好，我们注意到您尚未填写本次随访项目的评估问卷。该问卷对评估您的康复情况非常重要，请您尽快完成。如需协助，请联系我们。",
          },
          {
            title: "项目阶段总结",
            content:
              "您好，您参与的随访项目当前阶段已经完成。根据评估结果，您的恢复情况良好。我们已经为您制定了下一阶段的随访计划，详情请查看随访系统通知或咨询您的主治医生。",
          },
          {
            title: "随访计划调整通知",
            content:
              "您好，经项目组讨论，您的随访计划已进行调整，新的随访时间为XX月XX日，请您按时参加。如有时间冲突，请及时与我们联系。",
          },
        ],
  
        // 转发选项
        forwardOptions: [
          { label: "张医生（心内科）", value: "doctor1" },
          { label: "王医生（消化科）", value: "doctor2" },
          { label: "李医生（肿瘤科）", value: "doctor3" },
          { label: "刘管理师（随访中心）", value: "manager1" },
          { label: "孙管理师（康复部）", value: "manager2" },
        ],
  
        // 协作人选项
        staffOptions: [
          { label: "张医生（心内科）", value: "张医生" },
          { label: "王医生（消化科）", value: "王医生" },
          { label: "李医生（肿瘤科）", value: "李医生" },
          { label: "刘管理师（随访中心）", value: "刘管理师" },
          { label: "孙管理师（康复部）", value: "孙管理师" },
        ],
  
        // 默认项目活动
        defaultProjectActivities: [
          { content: "项目启动，患者入组", time: "2024-05-01", color: "#409EFF" },
          { content: "完成基线评估", time: "2024-05-05", color: "#67C23A" },
          { content: "第一阶段随访完成", time: "2024-05-15", color: "#67C23A" },
          { content: "第二阶段随访计划中", time: "2024-05-20", color: "#E6A23C" },
        ],
  
        // 患者列表数据
        patients: [
          {
            id: "P001",
            name: "许昊然",
            age: 65,
            gender: "male",
            recordNum: "HF20245001",
            phone: "13812345678",
            planStatus: "进行中",
            status: "正常",
            phase: "项目阶段2",
            diagnosis: "冠心病，高血压",
            project: "心脏康复项目",
            satisfaction: 4,
            compliance: 85,
            followUpCompletion: 80,
            projectProgress: 65,
            nextFollowUp: "2024-06-15",
            avatar:
              "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 2,
            projectActivities: [
              {
                content: "项目启动，患者入组",
                time: "2024-05-01",
                color: "#409EFF",
              },
              { content: "完成基线评估", time: "2024-05-05", color: "#67C23A" },
              {
                content: "第一阶段随访完成",
                time: "2024-05-15",
                color: "#67C23A",
              },
              {
                content: "第二阶段随访进行中",
                time: "2024-05-20",
                color: "#E6A23C",
              },
            ],
            abnormalAlerts: [
              {
                id: "A001",
                title: "血压异常波动",
                level: "warning",
                description: "患者血压近期波动较大，最高达到150/95",
                solution: "",
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content:
                  "主任，我想问一下我的随访计划什么时候结束？目前感觉状况还不错。",
                timestamp: new Date(2024, 5, 17, 9, 23),
                read: false,
              },
              {
                fromPatient: true,
                sender: "许昊然",
                role: "患者",
                content: "另外，我最近偶尔会有胸闷，这个需要去医院吗？",
                timestamp: new Date(2024, 5, 17, 9, 25),
                read: false,
              },
            ],
          },
          {
            id: "P002",
            name: "苏雅婷",
            age: 58,
            gender: "male",
            recordNum: "HF20245002",
            phone: "13987654321",
            planStatus: "进行中",
            status: "异常",
            phase: "项目阶段3",
            diagnosis: "2型糖尿病",
            project: "糖尿病管理项目",
            satisfaction: 3,
            compliance: 65,
            followUpCompletion: 60,
            projectProgress: 75,
            nextFollowUp: "2024-06-10",
            avatar:
              "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 1,
            abnormalAlerts: [
              {
                id: "A002",
                title: "随访依从性下降",
                level: "warning",
                description: "患者连续两次未按时完成随访问卷填写",
                solution: "",
              },
              {
                id: "A003",
                title: "血糖控制不佳",
                level: "danger",
                description: "患者空腹血糖持续>9.0mmol/L，需调整治疗方案",
                solution: "",
              },
            ],
            messages: [
              {
                fromPatient: false,
                sender: "赵主任",
                role: "项目管理师",
                content:
                  "李先生，您好！注意到您近期未能按时完成问卷填写，请问是遇到什么困难了吗？我们可以提供帮助。",
                timestamp: new Date(2024, 5, 15, 14, 30),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "苏雅婷",
                role: "患者",
                content:
                  "赵主任，抱歉，我最近工作比较忙，另外血糖控制有点困难，总是偏高。",
                timestamp: new Date(2024, 5, 16, 10, 20),
                read: false,
              },
            ],
          },
          {
            id: "P003",
            name: "林书辰",
            age: 72,
            gender: "male",
            recordNum: "HF20245003",
            phone: "13712345678",
            planStatus: "进行中",
            status: "异常",
            phase: "项目阶段1",
            diagnosis: "肺癌术后",
            project: "肿瘤随访项目",
            satisfaction: 5,
            compliance: 90,
            followUpCompletion: 85,
            projectProgress: 40,
            nextFollowUp: "2024-06-05",
            avatar:
              "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
            unreadCount: 0,
            abnormalAlerts: [
              {
                id: "A004",
                title: "术后恢复异常",
                level: "danger",
                description: "患者伤口有轻微感染迹象，需密切监控",
                solution: "已联系医生，建议患者3天内前往门诊处理",
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "林书辰",
                role: "患者",
                content: "赵主任，我的伤口好像有些发红，有点疼。",
                timestamp: new Date(2024, 5, 14, 9, 10),
                read: true,
              },
              {
                fromPatient: false,
                sender: "赵主任",
                role: "项目管理师",
                content:
                  "王先生，您好！根据您描述的情况，伤口可能有轻微感染，我已经与李医生沟通，建议您尽快前往医院门诊处理。我已经为您预约了明天上午10点的门诊，请您准时前往。",
                timestamp: new Date(2024, 5, 14, 10, 15),
                read: true,
                status: "replied",
                collaborators: ["李医生"],
              },
              {
                fromPatient: true,
                sender: "林书辰",
                role: "患者",
                content: "好的，谢谢赵主任，我明天会准时去医院。",
                timestamp: new Date(2024, 5, 14, 11, 30),
                read: true,
              },
              {
                fromPatient: false,
                sender: "李医生",
                role: "主治医师",
                content:
                  "王先生，我是李医生，已收到您伤口情况的消息。明天请带上您的病历卡和之前的用药记录，我会详细检查您的伤口情况。",
                timestamp: new Date(2024, 5, 14, 14, 15),
                read: true,
                forwardInfo: {
                  title: "由赵主任转发",
                  type: "info",
                  desc: "已将患者伤口情况通知李医生并安排明天门诊",
                },
              },
            ],
          },
          {
            id: "P004",
            name: "程若曦",
            age: 45,
            gender: "female",
            recordNum: "HF20245004",
            phone: "13612345678",
            planStatus: "暂停",
            status: "异常",
            phase: "项目阶段2",
            diagnosis: "甲状腺功能亢进",
            project: "内分泌疾病项目",
            satisfaction: 4,
            compliance: 75,
            followUpCompletion: 70,
            projectProgress: 60,
            nextFollowUp: "待定",
            avatar:
              "https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png",
            unreadCount: 0,
            abnormalAlerts: [
              {
                id: "A005",
                title: "项目暂停",
                level: "danger",
                description: "患者因个人原因申请暂停项目参与",
                solution: "已记录患者申请，暂停随访计划1个月",
              },
            ],
            messages: [
              {
                fromPatient: true,
                sender: "程若曦",
                role: "患者",
                content:
                  "赵主任，我因为要出差一个月，这段时间不方便参加随访，能否暂停项目？",
                timestamp: new Date(2024, 5, 10, 16, 20),
                read: true,
              },
              {
                fromPatient: false,
                sender: "赵主任",
                role: "项目管理师",
                content:
                  "赵女士，您好！我们理解您的情况。根据项目规定，可以为您暂停随访计划1个月。但请注意，出差期间仍需按医嘱服药，并记录身体状况。如有任何不适，请及时联系我们。出差结束后，我们将恢复您的随访计划。",
                timestamp: new Date(2024, 5, 10, 17, 15),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "程若曦",
                role: "患者",
                content: "谢谢理解，我会按照医嘱服药，有问题一定联系您。",
                timestamp: new Date(2024, 5, 10, 17, 30),
                read: true,
              },
            ],
          },
          {
            id: "P005",
            name: "郭子轩",
            age: 68,
            gender: "male",
            recordNum: "HF20245005",
            phone: "13512345678",
            planStatus: "已完成",
            status: "正常",
            phase: "项目结束",
            diagnosis: "前列腺增生",
            project: "泌尿系统疾病项目",
            satisfaction: 5,
            compliance: 95,
            followUpCompletion: 100,
            projectProgress: 100,
            nextFollowUp: "项目已结束",
            avatar:
              "https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9cpng.png",
            unreadCount: 1,
            abnormalAlerts: [],
            projectActivities: [
              {
                content: "项目启动，患者入组",
                time: "2024-01-05",
                color: "#409EFF",
              },
              { content: "完成基线评估", time: "2024-01-10", color: "#67C23A" },
              {
                content: "第一阶段随访完成",
                time: "2024-02-15",
                color: "#67C23A",
              },
              {
                content: "第二阶段随访完成",
                time: "2024-03-20",
                color: "#67C23A",
              },
              {
                content: "第三阶段随访完成",
                time: "2024-04-25",
                color: "#67C23A",
              },
              {
                content: "项目结束评估完成",
                time: "2024-05-10",
                color: "#67C23A",
                size: "large",
              },
            ],
            messages: [
              {
                fromPatient: false,
                sender: "赵主任",
                role: "项目管理师",
                content:
                  "钱先生，您好！恭喜您成功完成了为期4个月的随访项目。根据最终评估结果，您的治疗效果非常理想，各项指标均恢复正常。感谢您的积极配合！如后续有任何问题，您仍可以联系我们。",
                timestamp: new Date(2024, 5, 15, 14, 0),
                read: true,
                status: "replied",
              },
              {
                fromPatient: true,
                sender: "郭子轩",
                role: "患者",
                content:
                  "谢谢赵主任和整个医疗团队的悉心照顾，我现在感觉比以前好多了。我想问一下，虽然项目结束了，我还需要定期去医院复查吗？",
                timestamp: new Date(2024, 5, 18, 9, 30),
                read: false,
              },
            ],
          },
        ],
      };
    },
    computed: {
      filteredPatients() {
        let result = this.patients;
  
        // 标签筛选
        if (this.patientListTab === "pending") {
          result = result.filter((patient) => patient.unreadCount > 0);
        } else if (this.patientListTab === "abnormal") {
          result = result.filter((patient) => patient.status === "异常");
        }
  
        // 项目筛选
        if (this.projectFilter) {
          result = result.filter(
            (patient) =>
              patient.project && patient.project.includes(this.projectFilter)
          );
        }
  
        // 疾病分类筛选
        if (this.diseaseFilter) {
          // 这里可以根据实际情况做更复杂的匹配逻辑
          const diseaseMap = {
            heart: ["冠心病", "高血压", "心脏"],
            diabetes: ["糖尿病", "内分泌"],
            cancer: ["肿瘤", "癌"],
            neuro: ["神经", "卒中"],
            respiratory: ["呼吸", "肺"],
          };
  
          const keywords = diseaseMap[this.diseaseFilter] || [];
          result = result.filter((patient) =>
            keywords.some(
              (keyword) =>
                patient.diagnosis && patient.diagnosis.includes(keyword)
            )
          );
        }
  
        // 关键词搜索
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          result = result.filter(
            (patient) =>
              patient.name.toLowerCase().includes(query) ||
              patient.recordNum.toLowerCase().includes(query)
          );
        }
  
        return result;
      },
  
      // 判断是否可以发送回复
      canSendReply() {
        return (
          (this.replyContent || this.uploadFiles.length > 0) &&
          this.selectedPatient
        );
      },
  
      // 获取已@人员的名字列表
      mentionedStaffNames() {
        return this.mentionedStaff.map((staffId) => {
          const staff = this.staffOptions.find(
            (option) => option.value === staffId
          );
          return staff ? staff.label.split("（")[0] : staffId;
        });
      },
    },
    methods: {
      // 患者列表相关方法
      filterPatients() {
        // 搜索过滤逻辑在computed中实现
      },
  
      handleTabClick() {
        // 切换标签逻辑在computed中实现
      },
  
      selectPatient(patient) {
        this.selectedPatient = patient;
        this.forwardTarget = null;
        this.replyContent = "";
        this.uploadFiles = [];
        this.mentionedStaff = [];
        this.nextFollowUpDate = null;
        this.followUpType = "";
  
        // 标记该患者的所有消息为已读
        if (patient && patient.messages) {
          patient.messages.forEach((msg) => {
            if (msg.fromPatient) {
              msg.read = true;
            }
          });
          // 重置未读数
          patient.unreadCount = 0;
        }
      },
  
      getPendingCount() {
        return this.patients.filter((patient) => patient.unreadCount > 0).length;
      },
  
      getPatientUnreadCount(patient) {
        return patient.messages.filter((msg) => msg.fromPatient && !msg.read)
          .length;
      },
  
      getLatestMessage(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          // 去掉HTML标签，截取内容
          const content = lastMsg.content.replace(/<[^>]*>/g, "");
          return content.length > 20 ? content.substring(0, 20) + "..." : content;
        }
        return "无消息记录";
      },
  
      getLatestMessageTime(patient) {
        if (patient.messages && patient.messages.length > 0) {
          const lastMsg = patient.messages[patient.messages.length - 1];
          return this.formatTime(lastMsg.timestamp);
        }
        return "";
      },
  
      // 消息区域相关方法
      showDateDivider(message, index) {
        if (index === 0) return true;
  
        const prevMessage = this.selectedPatient.messages[index - 1];
        const prevDate = new Date(prevMessage.timestamp).setHours(0, 0, 0, 0);
        const currDate = new Date(message.timestamp).setHours(0, 0, 0, 0);
  
        return prevDate !== currDate;
      },
  
      formatFullDate(date) {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const weekDays = ["日", "一", "二", "三", "四", "五", "六"];
        return `${year}年${month}月${day}日 星期${weekDays[d.getDay()]}`;
      },
  
      formatTime(date) {
        if (!date) return "";
        const d = new Date(date);
        const today = new Date().setHours(0, 0, 0, 0);
        const messageDay = new Date(d).setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
  
        if (messageDay === today) {
          return this.formatHourMinute(d);
        } else if (messageDay === yesterday.getTime()) {
          return "昨天";
        } else {
          const month = d.getMonth() + 1;
          const day = d.getDate();
          return `${month}/${day}`;
        }
      },
  
      formatHourMinute(date) {
        if (!date) return "";
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}`;
      },
  
      // 文件上传相关方法
      handleUploadImage() {
        this.$message.info("请选择要上传的图片");
        // 模拟上传图片
        setTimeout(() => {
          this.uploadFiles.push({
            name: "图片" + (Math.floor(Math.random() * 1000) + 1) + ".jpg",
            url: "https://shadow.elemecdn.com/a/17/d43b41abc8c413096227590fb94aejpeg.jpeg",
            type: "image",
          });
        }, 500);
      },
  
      handleUploadFile() {
        this.$message.info("请选择要上传的文件");
        // 模拟上传文件
        setTimeout(() => {
          this.uploadFiles.push({
            name: "随访记录" + (Math.floor(Math.random() * 1000) + 1) + ".pdf",
            url: "#",
            type: "file",
            size: Math.floor(Math.random() * 900 + 100) + "KB",
          });
        }, 500);
      },
  
      removeUploadFile(index) {
        this.uploadFiles.splice(index, 1);
      },
  
      // 快捷模板相关方法
      handleTemplateCommand(command) {
        if (command === "manage") {
          this.templateDialogVisible = true;
        } else {
          this.replyContent = this.replyTemplates[command].content;
        }
      },
  
      // @ 协作人相关方法
      handleMentionCommand(staff) {
        // 添加到被@的人员列表中
        if (!this.mentionedStaff.includes(staff)) {
          this.mentionedStaff.push(staff);
        }
  
        // 在内容中添加@标记
        const staffName = this.staffOptions
          .find((option) => option.value === staff)
          .label.split("（")[0];
        this.replyContent += `@${staffName} `;
      },
  
      handleForwardCommand(command) {
        this.forwardTarget = command;
        this.$message.info(`留言将转发给 ${this.getForwardTargetName(command)}`);
      },
  
      getForwardTargetName(targetId) {
        const target = this.forwardOptions.find((opt) => opt.value === targetId);
        return target ? target.label : "未知接收人";
      },
  
      sendReply() {
        if (!this.selectedPatient) return;
  
        // 构建新消息对象
        const newMessage = {
          fromPatient: false,
          sender: this.currentManager.name,
          role: this.currentManager.title,
          content: this.replyContent || "已收到附件",
          timestamp: new Date(),
          read: true,
          status: this.forwardTarget ? "forwarded" : "replied",
        };
  
        // 如果有被@的人员
        if (this.mentionedStaff.length > 0) {
          newMessage.collaborators = this.mentionedStaff.map((staffId) => {
            const staff = this.staffOptions.find(
              (option) => option.value === staffId
            );
            return staff ? staff.label.split("（")[0] : staffId;
          });
        }
  
        // 如果是转发
        if (this.forwardTarget) {
          const targetName = this.getForwardTargetName(this.forwardTarget);
          newMessage.forwardInfo = {
            title: `已转发给 ${targetName}`,
            type: "info",
            desc: `已将患者留言转发给${targetName}处理`,
          };
        }
  
        // 如果有上传文件，添加到消息中
        if (this.uploadFiles.length > 0) {
          const attachments = this.uploadFiles
            .filter((file) => file.type === "file")
            .map((file) => ({
              name: file.name,
              url: file.url || "#",
              size: file.size || "0KB",
            }));
  
          if (attachments.length > 0) {
            newMessage.attachments = attachments;
          }
  
          const images = this.uploadFiles
            .filter((file) => file.type === "image")
            .map((file) => file.url);
          if (images.length > 0) {
            newMessage.images = images;
          }
        }
  
        // 添加到消息列表
        this.selectedPatient.messages.push(newMessage);
  
        // 如果是转发并且需要模拟医生回复
        if (this.forwardTarget && this.forwardTarget.startsWith("doctor")) {
          // 延迟2秒后模拟医生回复
          setTimeout(() => {
            const doctorName =
              this.forwardTarget === "doctor1"
                ? "张医生"
                : this.forwardTarget === "doctor2"
                ? "王医生"
                : "李医生";
  
            const doctorReply = {
              fromPatient: false,
              sender: doctorName,
              role: "主治医师",
              content: `收到项目组转发的信息。已了解患者${this.selectedPatient.name}的情况，我会尽快评估并提供专业建议。请告知患者我会在24小时内联系他们，如有紧急情况请直接来门诊就诊。`,
              timestamp: new Date(Date.now() + 120000), // 2分钟后
              read: true,
              forwardInfo: {
                title: `由${this.currentManager.name}转发`,
                type: "info",
                desc: "项目管理师已转发患者情况请求专业评估",
              },
            };
  
            this.selectedPatient.messages.push(doctorReply);
          }, 2000);
        }
  
        // 清空回复框
        this.replyContent = "";
        this.uploadFiles = [];
        this.forwardTarget = null;
        this.mentionedStaff = [];
  
        // 提示发送成功
        this.$message.success("回复已发送");
  
        // 滚动到底部
        this.scrollToBottom();
      },
  
      downloadAttachment(attachment) {
        this.$message.info(`正在下载: ${attachment.name}`);
        // 实际项目中这里应该是真实下载逻辑
      },
  
      // 患者操作相关方法
      getStatusType(status) {
        switch (status) {
          case "异常":
            return "danger";
          case "暂停":
            return "warning";
          case "进行中":
            return "primary";
          case "已完成":
            return "success";
          default:
            return "info";
        }
      },
  
      getComplianceColor(value) {
        if (value >= 90) return "#67C23A"; // 优秀
        if (value >= 80) return "#409EFF"; // 良好
        if (value >= 70) return "#E6A23C"; // 一般
        return "#F56C6C"; // 需关注
      },
  
      handlePatientAction(command) {
        if (!this.selectedPatient) return;
  
        switch (command) {
          case "phone":
            this.$message.info(
              `正在拨打患者${this.selectedPatient.name}的电话: ${this.selectedPatient.phone}`
            );
            break;
          case "record":
            this.$message.info(`查看${this.selectedPatient.name}的随访记录`);
            break;
          case "plan":
            this.$message.info(`查看${this.selectedPatient.name}的随访计划`);
            break;
          // ...existing code...
  
          case "report":
            this.$message.info(`查看${this.selectedPatient.name}的项目报告`);
            break;
        }
      },
  
      // 右侧快捷操作区功能
      sendQuestionnaire() {
        this.questionnaireDialogVisible = true;
        this.selectedQuestionnaire = "";
        this.questionnaireDeadline = new Date();
        this.questionnaireDeadline.setDate(
          this.questionnaireDeadline.getDate() + 7
        ); // 默认截止日期为一周后
        this.reminderMethods = ["sms", "app"];
        this.questionnaireRemark = "";
      },
  
      confirmSendQuestionnaire() {
        if (!this.selectedQuestionnaire) {
          this.$message.warning("请选择问卷类型");
          return;
        }
  
        const questionnaire = this.questionnaireOptions.find(
          (q) => q.value === this.selectedQuestionnaire
        );
        this.$message.success(
          `问卷"${questionnaire.label}"已成功发送给${this.selectedPatient.name}`
        );
  
        // 添加到消息记录中
        const newMessage = {
          fromPatient: false,
          sender: this.currentManager.name,
          role: this.currentManager.title,
          content: `已向您发送"${
            questionnaire.label
          }"问卷，请在${this.formatFullDate(
            this.questionnaireDeadline
          )}之前完成填写。${
            this.questionnaireRemark ? "备注：" + this.questionnaireRemark : ""
          }`,
          timestamp: new Date(),
          read: true,
        };
  
        this.selectedPatient.messages.push(newMessage);
        this.questionnaireDialogVisible = false;
        this.scrollToBottom();
      },
  
      sendScaleForm() {
        this.$message.success(`已向${this.selectedPatient.name}发送评估量表`);
      },
  
      sendSmsNotification() {
        this.$prompt("请输入短信内容", "发送短信通知", {
          confirmButtonText: "发送",
          cancelButtonText: "取消",
          inputType: "textarea",
          inputValue: `${this.selectedPatient.name}您好，提醒您按时参加随访项目活动，如有问题请联系我们。`,
        })
          .then(({ value }) => {
            this.$message({
              type: "success",
              message: `短信已发送至 ${this.selectedPatient.phone}`,
            });
          })
          .catch(() => {});
      },
  
      updateFollowUpPlan() {
        if (!this.nextFollowUpDate) {
          this.$message.warning("请选择下次随访日期");
          return;
        }
  
        if (!this.followUpType) {
          this.$message.warning("请选择随访方式");
          return;
        }
  
        const followUpTypeMap = {
          clinic: "门诊随访",
          phone: "电话随访",
          online: "线上随访",
          home: "家访",
        };
  
        this.$confirm(
          `确定将${
            this.selectedPatient.name
          }的下次随访计划更新为${this.formatFullDate(
            this.nextFollowUpDate
          )}，随访方式为${followUpTypeMap[this.followUpType]}?`,
          "确认信息",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning",
          }
        )
          .then(() => {
            // 更新患者随访信息
            this.selectedPatient.nextFollowUp = this.formatDate(
              this.nextFollowUpDate
            );
  
            // 添加到消息记录中
            const newMessage = {
              fromPatient: false,
              sender: this.currentManager.name,
              role: this.currentManager.title,
              content: `您的随访计划已更新，下次随访时间为${this.formatFullDate(
                this.nextFollowUpDate
              )}，随访方式为${
                followUpTypeMap[this.followUpType]
              }。请您按时参加，如有时间冲突，请提前联系我们。`,
              timestamp: new Date(),
              read: true,
            };
  
            this.selectedPatient.messages.push(newMessage);
            this.$message.success("随访计划已更新");
            this.scrollToBottom();
          })
          .catch(() => {});
      },
  
      assignDoctor() {
        this.assignDoctorDialogVisible = true;
        this.selectedDoctor = "";
        this.assignReason = "";
        this.notifyAssignee = true;
      },
  
      confirmAssignDoctor() {
        if (!this.selectedDoctor) {
          this.$message.warning("请选择医生");
          return;
        }
  
        const doctor = this.doctorOptions.find(
          (d) => d.value === this.selectedDoctor
        );
        this.$message.success(
          `已成功将患者${this.selectedPatient.name}分配给${doctor.label}`
        );
  
        // 可以在这里添加更多逻辑，比如更新患者的主治医生信息
  
        this.assignDoctorDialogVisible = false;
      },
  
      assignManager() {
        this.$message.info(`分配个案管理师功能开发中`);
        // 这里可以添加与assignDoctor类似的逻辑
      },
  
      reportIssue() {
        this.issueReportDialogVisible = true;
        this.issueType = "";
        this.issueSeverity = 3;
        this.issueDescription = "";
        this.issueSuggestion = "";
        this.issueNotifyTargets = ["doctor", "manager"];
      },
  
      confirmReportIssue() {
        if (!this.issueType) {
          this.$message.warning("请选择异常类型");
          return;
        }
  
        if (!this.issueDescription) {
          this.$message.warning("请填写异常描述");
          return;
        }
  
        const issueTypeMap = {
          compliance: "随访依从性异常",
          vital_signs: "生命体征异常",
          adverse_event: "不良反应",
          process: "项目执行异常",
          other: "其他异常",
        };
  
        this.$message.success(
          `已成功报告"${issueTypeMap[this.issueType]}"异常情况`
        );
  
        // 添加到患者的异常提醒中
        if (!this.selectedPatient.abnormalAlerts) {
          this.selectedPatient.abnormalAlerts = [];
        }
  
        this.selectedPatient.abnormalAlerts.push({
          id: "A" + Math.floor(Math.random() * 1000),
          title: issueTypeMap[this.issueType],
          level:
            this.issueSeverity > 3
              ? "danger"
              : this.issueSeverity > 2
              ? "warning"
              : "info",
          description: this.issueDescription,
          solution: this.issueSuggestion,
        });
  
        // 更新患者状态为异常
        this.selectedPatient.status = "异常";
  
        this.issueReportDialogVisible = false;
      },
  
      // 模板管理相关方法
      addNewTemplate() {
        this.currentTemplateAction = "add";
        this.currentTemplateIndex = -1;
        this.currentTemplate = {
          title: "",
          content: "",
        };
        this.editTemplateDialogVisible = true;
      },
  
      editTemplate(template) {
        this.currentTemplateAction = "edit";
        this.currentTemplateIndex = this.replyTemplates.findIndex(
          (t) => t.title === template.title
        );
        this.currentTemplate = { ...template };
        this.editTemplateDialogVisible = true;
      },
  
      deleteTemplate(index) {
        this.$confirm("确定要删除此模板吗?", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            this.replyTemplates.splice(index, 1);
            this.$message.success("模板已删除");
  
            // 保存到本地存储
            localStorage.setItem(
              "projectManagerReplyTemplates",
              JSON.stringify(this.replyTemplates)
            );
          })
          .catch(() => {});
      },
  
      saveTemplate() {
        if (!this.currentTemplate.title || !this.currentTemplate.content) {
          this.$message.warning("模板标题和内容不能为空");
          return;
        }
  
        if (this.currentTemplateAction === "add") {
          // 检查是否有重名模板
          if (
            this.replyTemplates.some(
              (t) => t.title === this.currentTemplate.title
            )
          ) {
            this.$message.warning("已存在同名模板，请修改模板名称");
            return;
          }
          this.replyTemplates.push({ ...this.currentTemplate });
        } else {
          this.replyTemplates[this.currentTemplateIndex] = {
            ...this.currentTemplate,
          };
        }
  
        this.$message.success("模板已保存");
        this.editTemplateDialogVisible = false;
  
        // 保存到本地存储
        localStorage.setItem(
          "projectManagerReplyTemplates",
          JSON.stringify(this.replyTemplates)
        );
      },
  
      // 格式化日期为YYYY-MM-DD
      formatDate(date) {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      },
  
      // 滚动到底部
      scrollToBottom() {
        this.$nextTick(() => {
          const container = document.querySelector(".message-content");
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      },
    },
    mounted() {
      // 初始化时可以默认选中第一个患者
      if (this.patients && this.patients.length > 0) {
        this.selectPatient(this.patients[0]);
      }
  
      // 从本地存储加载模板数据
      const savedTemplates = localStorage.getItem("projectManagerReplyTemplates");
      if (savedTemplates) {
        try {
          this.replyTemplates = JSON.parse(savedTemplates);
        } catch (e) {
          console.error("加载回复模板失败", e);
        }
      }
    },
  };
  </script>
  
  <style scoped>
  /* 整体容器 */
  .message-container {
    display: flex;
    height: calc(100vh - 120px);
    background-color: #f5f7fa;
  }
  
  /* 左侧患者列表样式 */
  .patient-list {
    width: 280px;
    border-right: 1px solid #ebeef5;
    display: flex;
    flex-direction: column;
    background-color: #fff;
  }
  
  .search-container {
    padding: 15px;
  }
  
  .filter-section {
    padding: 0 15px 10px 15px;
  }
  
  .patient-list-content {
    flex: 1;
    overflow-y: auto;
  }
  
  .patient-item {
    padding: 12px 15px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
  
  .patient-item:hover {
    background-color: #f5f7fa;
  }
  
  .patient-item.active {
    background-color: #ecf5ff;
  }
  
  .patient-item.abnormal-patient {
    background-color: #fff8f8;
  }
  
  .avatar-container {
    position: relative;
    margin-right: 12px;
  }
  
  .item-unread {
    position: absolute;
    top: -5px;
    right: -5px;
  }
  
  .item-abnormal {
    position: absolute;
    bottom: -5px;
    right: -5px;
    border-radius: 4px;
  }
  
  .patient-info {
    flex: 1;
    min-width: 0;
  }
  
  .patient-name {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  }
  
  .patient-project {
    font-size: 12px;
    color: #606266;
    margin-left: 5px;
  }
  
  .patient-disease {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
  }
  
  .latest-message {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .message-time {
    font-size: 12px;
    color: #c0c4cc;
    white-space: nowrap;
  }
  
  /* 中间消息区域样式 */
  .message-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border-right: 1px solid #ebeef5;
  }
  
  .message-header {
    padding: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #ebeef5;
  }
  
  .patient-title {
    display: flex;
    align-items: center;
  }
  
  .patient-title .name {
    font-size: 16px;
    font-weight: 500;
    margin-right: 10px;
  }
  
  .patient-title .record-number {
    font-size: 13px;
    color: #909399;
    margin-right: 10px;
  }
  
  .message-content {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
  }
  
  .message-block {
    margin-bottom: 15px;
  }
  
  .message-date-divider {
    text-align: center;
    margin: 10px 0;
    font-size: 12px;
    color: #909399;
  }
  
  .message-date-divider span {
    background-color: #fff;
    padding: 0 10px;
  }
  
  .message {
    margin-bottom: 15px;
  }
  
  .patient-message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .staff-message {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  .message-sender {
    font-size: 12px;
    color: #606266;
    margin-bottom: 5px;
  }
  
  .sender-role {
    margin-left: 5px;
    color: #909399;
  }
  
  .message-time {
    margin-left: 8px;
    color: #c0c4cc;
  }
  
  .message-bubble {
    padding: 10px 15px;
    border-radius: 6px;
    max-width: 75%;
    word-break: break-word;
    position: relative;
  }
  
  .patient-message .message-bubble {
    background-color: #fff;
    border: 1px solid #ebeef5;
  }
  
  .staff-message .message-bubble {
    background-color: #ecf5ff;
    color: #409eff;
  }
  
  .message-content-text {
    font-size: 14px;
    line-height: 1.5;
  }
  
  .message-images {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
  }
  
  .message-image {
    width: 100px;
    height: 100px;
    margin-right: 10px;
    margin-bottom: 10px;
    border-radius: 4px;
    object-fit: cover;
    cursor: pointer;
  }
  
  .message-attachments {
    margin-top: 10px;
    background-color: #f5f7fa;
    border-radius: 4px;
    padding: 8px;
  }
  
  .attachment-item {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }
  
  .attachment-item:last-child {
    margin-bottom: 0;
  }
  
  .attachment-item i {
    margin-right: 5px;
    font-size: 16px;
    color: #909399;
  }
  
  .attachment-size {
    font-size: 12px;
    color: #c0c4cc;
    margin: 0 5px;
  }
  
  .message-status {
    margin-top: 8px;
    font-size: 12px;
  }
  
  .status-info {
    display: flex;
    align-items: center;
    color: #909399;
  }
  
  .status-info i {
    margin-right: 5px;
    font-size: 14px;
  }
  
  .forward-info {
    margin-top: 10px;
  }
  
  .collaborators-info {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }
  
  .collaborator-label {
    margin-right: 5px;
  }
  
  .message-reply {
    padding: 15px;
    border-top: 1px solid #ebeef5;
  }
  
  .reply-toolbar {
    display: flex;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  
  .upload-preview {
    margin-top: 10px;
  }
  
  .upload-file {
    background-color: #f5f7fa;
    border-radius: 4px;
    padding: 5px 10px;
    margin-bottom: 5px;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .forward-alert,
  .mention-alert {
    margin-top: 10px;
  }
  
  .reply-actions {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
  }
  
  .no-patient-selected {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #909399;
  }
  
  .no-patient-selected i {
    font-size: 60px;
    margin-bottom: 15px;
  }
  
  /* 右侧操作区域 */
  .operation-area {
    width: 300px;
    padding: 15px;
    overflow-y: auto;
    background-color: #fff;
  }
  
  .operation-header {
    margin-bottom: 15px;
    text-align: center;
  }
  
  .operation-header h3 {
    font-size: 18px;
    color: #303133;
  }
  
  .operation-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .plan-adjustment {
    margin-top: 15px;
  }
  
  .plan-item {
    margin-bottom: 10px;
  }
  
  .project-management {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 15px;
  }
  
  .patient-metrics {
    margin-top: 15px;
  }
  
  .metric-card {
    background-color: #f5f7fa;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 10px;
  }
  
  .metric-title {
    font-size: 12px;
    color: #606266;
    margin-bottom: 8px;
  }
  
  .metric-value {
    text-align: center;
  }
  
  .pending-badge {
    margin-top: -2px;
  }
  </style>
  